/**
 * useNotificationStream — reactive read hook for FR-004.
 *
 * Wraps `api.notifications.listForUser`. Calls `useSubscriptionActive()` and
 * passes the literal string `"skip"` to `useQuery` when inactive. Subscribes
 * reactively to the first page only; subsequent pages are fetched via
 * `useConvex().query()` (one-shot, non-reactive) and accumulated in local
 * `useState`. Memoizes the merged items via `useMemo`, the `loadMore` callback
 * via `useCallback`, and the return object via `useMemo` to honor FR-005's
 * reference-stability contract.
 *
 * Exposes `isLoadingMore` inside `NotificationStreamData`.
 *
 * **No error variant.** Convex `useQuery` throws propagate up the React tree
 * to the nearest `<ConvexErrorBoundary>`. Catching them in the hook would
 * duplicate React's error boundary mechanism (Finding B-3).
 *
 * All hooks are called unconditionally before any early returns (rules-of-hooks).
 *
 * @see specs/prd.md §3 FR-004
 * @see specs/prd.md §13.B-3
 * @see specs/prd.md §13.D-3
 */
import { useCallback, useMemo, useState } from "react";
import { useQuery, useConvex } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { transformNotification } from "../transforms/notification";
import { INBOX_PAGE_SIZE } from "../constants/inboxSizing";
import type { NotificationCursor, NotificationStreamState } from "../types/notification";
import type { Doc } from "../../../../../convex/_generated/dataModel";

/**
 * Shape returned by `api.notifications.listForUser`.
 */
interface ListForUserResult {
  items: Doc<"notifications">[];
  unreadCount: number;
  preferences: unknown[];
  nextCursor: NotificationCursor | null;
}

/**
 * Named hook providing a reactive notification stream with `loadMore` pagination.
 *
 * @returns `NotificationStreamState` — `loading | empty | ready`.
 */
export function useNotificationStream(): NotificationStreamState {
  const isActive = useSubscriptionActive();
  const convex = useConvex();

  // -- State for extra pages (non-reactive, accumulated via loadMore) ---------
  const [extraPages, setExtraPages] = useState<Doc<"notifications">[][]>([]);
  const [nextCursor, setNextCursor] = useState<NotificationCursor | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreState, setHasMoreState] = useState<boolean | null>(null);

  // -- Reactive subscription for first page -----------------------------------
  // All hooks called unconditionally before any early returns (rules-of-hooks).
  const firstPage = useQuery(
    api.notifications.listForUser,
    isActive ? { limit: INBOX_PAGE_SIZE } : "skip",
  ) as ListForUserResult | undefined;

  // -- loadMore callback (memoized via useCallback) ---------------------------
  const loadMore = useCallback(() => {
    // Determine the effective cursor: if extra pages have been loaded, use the
    // stored nextCursor from the last loadMore; otherwise use the first page's
    // nextCursor.
    const effectiveCursor = extraPages.length > 0 ? nextCursor : (firstPage?.nextCursor ?? null);

    if (!effectiveCursor || isLoadingMore) return;

    setIsLoadingMore(true);

    convex
      .query(api.notifications.listForUser, {
        limit: INBOX_PAGE_SIZE,
        cursor: effectiveCursor,
      })
      .then((result: ListForUserResult) => {
        setExtraPages((prev) => [...prev, result.items]);
        setNextCursor(result.nextCursor);
        setHasMoreState(result.nextCursor !== null);
        setIsLoadingMore(false);
      })
      .catch(() => {
        // loadMore errors are surfaced via isLoadingMore reset; the component
        // layer shows a "Couldn't load more" banner. console.error is reserved
        // for non-ConvexError exceptions per CLAUDE.md §5.
        setIsLoadingMore(false);
        console.error("notifications.loadMore.error"); // eslint-disable-line no-console
      });
  }, [convex, extraPages.length, nextCursor, firstPage?.nextCursor, isLoadingMore]);

  // -- Merge items: first page (reactive) + extra pages (accumulated) ---------
  const mergedItems = useMemo(() => {
    if (!firstPage) return [];
    const allDocs = [...firstPage.items];
    for (const page of extraPages) {
      allDocs.push(...page);
    }
    return allDocs.map(transformNotification);
  }, [firstPage, extraPages]);

  // -- Compute hasMore --------------------------------------------------------
  const hasMore = useMemo(() => {
    if (hasMoreState !== null) return hasMoreState;
    return firstPage?.nextCursor !== null && firstPage?.nextCursor !== undefined;
  }, [hasMoreState, firstPage?.nextCursor]);

  // -- Memoize the ready-state return object -----------------------------------
  // Called unconditionally (rules-of-hooks); consumed only when status is "ready".
  const readyState: NotificationStreamState = useMemo(
    () => ({
      status: "ready" as const,
      data: {
        items: mergedItems,
        unreadCount: firstPage?.unreadCount ?? 0,
        preferences: firstPage?.preferences ?? [],
        hasMore,
        isLoadingMore,
      },
      loadMore,
    }),
    [mergedItems, firstPage?.unreadCount, firstPage?.preferences, hasMore, isLoadingMore, loadMore],
  );

  // -- Return discriminated union ---------------------------------------------
  // Early returns AFTER all hooks have been called.

  if (!isActive || firstPage === undefined) {
    return { status: "loading" };
  }

  if (firstPage.items.length === 0 && extraPages.length === 0) {
    return { status: "empty" };
  }

  return readyState;
}
